/**
 * TeamFormSection - Reusable component for team selection and details in broadcast/game forms
 * Used in both BroadcastsTab (schedule game form) and past game form
 */
import { Wand2 } from "lucide-react";
import { ImageUpload } from "../ImageUpload";

export const TeamFormSection = ({ 
  team, 
  onTeamChange,
  onChange, // Alternative prop name for backward compatibility
  teams = [], 
  label = "Team",
  extractingColor = false,
  onExtractColor,
  onSetTBD,
  onSelectTeam, // Alternative prop for team selection
  allowTBD = false,
  testIdPrefix = "",
  maxprepsLink = "",
  onMaxprepsLinkChange,
}) => {
  // Support both prop names for team change handler
  const handleTeamUpdate = onTeamChange || onChange;
  
  const handleTeamSelect = (teamId) => {
    // If external handler provided, use it
    if (onSelectTeam) {
      onSelectTeam(teamId);
      return;
    }
    
    if (teamId === 'TBD' && onSetTBD) {
      onSetTBD(true);
      return;
    }
    
    if (teamId && handleTeamUpdate) {
      const selectedTeam = teams.find(t => t.id === teamId);
      if (selectedTeam) {
        handleTeamUpdate({
          name: selectedTeam.name,
          abbreviation: selectedTeam.abbreviation || "",
          mascot: selectedTeam.mascot || "",
          color: selectedTeam.primary_color || "#FFFFFF",
          logo_url: selectedTeam.logo_url || ""
        });
      }
    }
  };

  const updateField = (field, value) => {
    if (handleTeamUpdate) {
      handleTeamUpdate({ ...team, [field]: value });
    }
  };
  
  // Find current team ID based on team name for controlled select
  const getCurrentTeamId = () => {
    if (team.name === 'TBD') return 'TBD';
    const matchedTeam = teams.find(t => t.name === team.name);
    return matchedTeam ? matchedTeam.id : '';
  };

  return (
    <div className="p-4 bg-zinc-800/50 border border-white/5">
      <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400 mb-3">{label}</h4>
      <div className="space-y-3">
        {/* Team Selector */}
        {teams.length > 0 && (
          <div className="mb-3">
            <label className="block text-[10px] text-zinc-500 mb-1">Select Team</label>
            <div className="flex gap-2">
              <select 
                value={getCurrentTeamId()}
                onChange={(e) => handleTeamSelect(e.target.value)}
                className="w-full bg-zinc-900 border border-orange-500/30 text-white px-3 py-2 text-sm"
                data-testid={testIdPrefix ? `${testIdPrefix}-team-select` : 'team-select'}
              >
                <option value="">-- Quick Select Team --</option>
                {allowTBD && <option value="TBD">TBD (To Be Determined)</option>}
                {teams.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.mascot ? `(${t.mascot})` : ''} - {t.sport}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        
        {/* Team Name */}
        <input 
          type="text" 
          value={team.name} 
          onChange={(e) => updateField('name', e.target.value)} 
          placeholder="Team Name" 
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
        />
        
        {/* Abbreviation & Mascot */}
        <div className="grid grid-cols-2 gap-2">
          <input 
            type="text" 
            value={team.abbreviation} 
            onChange={(e) => updateField('abbreviation', e.target.value)} 
            placeholder="Abbrev (e.g., NYG)" 
            className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
          />
          <input 
            type="text" 
            value={team.mascot} 
            onChange={(e) => updateField('mascot', e.target.value)} 
            placeholder="Mascot" 
            className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        
        {/* Color Picker */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-zinc-500">Color:</label>
          <input 
            type="color" 
            value={team.color} 
            onChange={(e) => updateField('color', e.target.value)} 
            className="w-10 h-8 border-0 bg-transparent cursor-pointer" 
          />
          <span className="text-xs text-zinc-600">{team.color}</span>
          {team.logo_url && onExtractColor && (
            <button 
              type="button" 
              onClick={onExtractColor}
              disabled={extractingColor}
              className="ml-auto text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1 disabled:opacity-50"
            >
              <Wand2 className="w-3 h-3" />
              {extractingColor ? "Extracting..." : "Auto from logo"}
            </button>
          )}
        </div>
        
        {/* Logo Upload */}
        <ImageUpload
          value={team.logo_url}
          onChange={(url) => updateField('logo_url', url)}
          label="Team Logo"
          type="logo"
        />

        {/* MaxPreps Link */}
        {onMaxprepsLinkChange && (
          <div className="pt-2 border-t border-white/5">
            <label className="block text-[10px] text-zinc-500 mb-1">MaxPreps Link</label>
            <input
              type="text"
              value={maxprepsLink || ''}
              onChange={(e) => onMaxprepsLinkChange(e.target.value)}
              placeholder="https://www.maxpreps.com/tx/.../football/"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
              data-testid={testIdPrefix ? `${testIdPrefix}-maxpreps-link-input` : 'maxpreps-link-input'}
            />
            <p className="text-[10px] text-zinc-600 mt-1">Shows team schedule, record & staff on game pages without StatMoose</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamFormSection;
